import { Dialog } from "@mui/material"
import * as api from "api"
import CloseButton from "common/CloseButton"
import { memo } from "react"
import styled, { css } from "styled-components"
import { Navigation } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"

const buttonCSS = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: center;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin: 0;
  }
`
const StyledSwiper = styled(Swiper)`
  width: calc(100% - 60px);
  height: calc(100% - 60px);
  z-index: 0;
  margin: 30px;
  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`
const StyledCloseButton = styled(CloseButton)`
  top: 0;
  right: 0;
  svg {
    path {
      fill: white;
    }
  }
`
const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`
const SwiperButton = styled.a<{ $visible: boolean }>`
  font-family: swiper-icons;
  text-transform: none !important;
  letter-spacing: 0;
  font-size: 55px;
  cursor: pointer;
  user-select: none;
  color: white;
  visibility: ${(p) => (p.$visible ? "visible" : "hidden")};
  &.swiper-button-disabled {
    opacity: 0.3;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    display: none;
  }
`
const StyledButtonNext = styled(SwiperButton)`
  /* ${buttonCSS} */
  position: absolute;
  top: 50%;
  left: 30px;
  :after {
    content: "prev";
  }
`
const StyledButtonPrev = styled(SwiperButton)`
  /* ${buttonCSS} */
  position: absolute;
  top: 50%;
  right: 30px;

  :after {
    content: "next";
  }
`

const StyledIframeWrap = styled.div`
  max-width: 100%;
  width: 1280px;
  position: relative;
`

const StyledIframeContainer = styled.div`
  height: 0;
  width: 100%;
  padding-bottom: calc(100% / (16 / 9));
  position: relative;
  iframe {
    width: 100%;
    height: 100%;
    max-height: 100vh;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const getYoutubeId = (url) => {
  if (!url) return null
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)

  return match && match[2].length === 11 ? match[2] : null
}

type ServiceCenterGalleryDialogProps = {
  onClose: () => void
  images: api.ImageModel[]
  videos: api.ImageModel[]
  active: number | null
}

const ServiceCenterGalleryDialog = ({
  onClose,
  images,
  videos,
  active,
}: ServiceCenterGalleryDialogProps) => {
  const nextEl = "SCGallery-nextEl"
  const prevEl = "SCGallery-prevEl"
  const plural = [...images, ...videos].length > 1
  return (
    <Dialog
      open={active != null}
      onClose={onClose}
      PaperProps={{
        style: { backgroundColor: "transparent" },
      }}
      fullScreen
      role={"dialog"}
      aria-modal={true}
      tabIndex={-1}
    >
      <StyledCloseButton onClick={onClose} aria-label="close window" />
      <StyledSwiper
        initialSlide={active ?? 0}
        slidesPerView={1}
        grabCursor
        modules={[Navigation]}
        navigation={{ nextEl: `.${nextEl}`, prevEl: `.${prevEl}` }}
      >
        {images.map((item, i) => (
          <SwiperSlide key={i}>
            <StyledImage
              src={`${item.imageUrl}?width=1280`}
              alt={item.alt || "service center gallery image"}
            />
          </SwiperSlide>
        ))}
        {videos.map((item, i) => {
          const videoId = getYoutubeId(item.imageUrl)
          const videoUrl = videoId ? `//www.youtube.com/embed/${videoId}` : null
          if (!videoUrl) return null
          return (
            <SwiperSlide key={i}>
              {({ isActive }) =>
                isActive &&
                videoUrl && (
                  <StyledIframeWrap>
                    <StyledIframeContainer>
                      <iframe src={videoUrl} frameBorder="0" />
                    </StyledIframeContainer>
                  </StyledIframeWrap>
                )
              }
            </SwiperSlide>
          )
        })}
      </StyledSwiper>
      <StyledButtonNext
        $visible={plural}
        className={nextEl}
        aria-label="לתמונה הבאה"
      />
      <StyledButtonPrev
        $visible={plural}
        className={prevEl}
        aria-label="לתמונה הקודמת"
      />
    </Dialog>
  )
}

export default memo(ServiceCenterGalleryDialog)
